import { apiUrl } from "../api/endpoints";
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "../Components/modal.css";
import axios from "axios";
import Select from "react-select";
import Alert from "../Components/Alert";
import Spinner from "../Components/Spinner";
import { useAuth } from "../hooks/AuthProvider";
import { LanguageContext } from "../context/LanguageContext";
import assetTranslations from "../languages/asset.json";
import { getErrorMessage } from "../utils/errorUtils";
import { AddNewIcon } from "../svg/UtilsIcons";
import { StepCircle } from "../svg/AssetCardIcon";
import { buildDeviceDropdownOption } from "../utils/dropdownLabels";
import {
  COMPRESSOR_FIELDS,
  DEFAULT_ASSET_CARD_CONFIG,
  FAN_FIELDS,
  getOperationalFieldsForAsset,
  getAssetTypesForCategory,
  normalizeAssetCardConfig,
  parseOptionalInteger,
  parseOptionalNumber,
} from "../utils/assetCardConfig";

const baseOperational = {
  compressors: [],
  indoor_fans: [],
  outdoor_fans: [],
};

const createInitialFormData = () => ({
  device_id: null,
  asset_name: "",
  asset_code: "",
  category_subcategory: "",
  brand: "",
  model_number: "",
  serial_number: "",
  type: "",
  manufacturer: "",
  manufacture_date: "",
  location: "",
  ac_capacity: "",
  operational: { ...baseOperational },
  maintenance: {
    installation_date: "",
    warranty_expiry_date: "",
    condition_status: "",
    technical_evaluation: "",
    criticality_level: "",
    estimated_life_cycle_years: "",
    last_maintenance_date: "",
    next_maintenance_due: "",
    o_and_m_vendor: "",
    depreciation: "",
  },
  financial: {
    supplier_vendor: "",
    department_owner: "",
    purchase_cost: "",
    annual_om_cost: "",
  },
  iot: {
    monitored_parameters: "",
    alert_thresholds: "",
    integration_protocol: "",
  },
});

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "var(--rs-control-bg)",
    borderColor: state.isFocused ? "var(--rs-control-border-focus)" : "var(--rs-control-border)",
    boxShadow: state.isFocused ? "var(--rs-control-focus-shadow)" : "none",
    color: "var(--rs-text)",
    minHeight: "44px",
    borderRadius: 8,
    cursor: "pointer",
  }),
  singleValue: (provided) => ({ ...provided, color: "var(--rs-text)" }),
  input: (provided) => ({ ...provided, color: "var(--rs-text)" }),
  placeholder: (provided) => ({ ...provided, color: "var(--rs-placeholder)" }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "var(--rs-menu-bg)",
    border: "1px solid var(--rs-menu-border)",
    borderRadius: 8,
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "var(--rs-option-selected-bg)"
      : state.isFocused
      ? "var(--rs-option-hover-bg)"
      : "var(--rs-option-bg)",
    color: state.isSelected ? "var(--rs-option-selected-text)" : "var(--rs-text)",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : "");
const formatValue = (value) => (value === null || value === undefined ? "" : value);

const sanitizeObject = (value) => {
  const result = {};
  Object.entries(value || {}).forEach(([key, item]) => {
    if (item === "") result[key] = null;
    else result[key] = item;
  });
  return result;
};

const sanitizeOperational = (operational = {}) => {
  const result = {};
  Object.entries(operational).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      result[key] = value.map((row) => sanitizeObject(row));
    } else if (value === "") {
      result[key] = null;
    } else {
      result[key] = value;
    }
  });
  return result;
};

const createBlankGroupRow = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.name] = "";
    return acc;
  }, {});

const syncRowsToCount = (rows, count, fields) => {
  const nextCount = Math.max(0, Math.trunc(Number(count) || 0));
  return Array.from({ length: nextCount }, (_, index) => rows?.[index] || createBlankGroupRow(fields));
};

const AssetCardFormModal = ({ mode, assetId, refresh, setState, onAlert }) => {
  const { org, permissions } = useAuth();
  const { lang } = useContext(LanguageContext);
  const tRoot = assetTranslations[lang] || assetTranslations.en;
  const t = mode === "edit" ? tRoot.editAsset : tRoot.addAsset;
  const addT = tRoot.addAsset;
  const isRtl = ["ar", "ur"].includes(lang);
  const canSubmit = mode === "edit" ? permissions.edit_assets : permissions.create_assets;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(createInitialFormData);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [assetConfig, setAssetConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitting, setSubmitting] = useState(false);
  const [alertState, setAlertState] = useState({ open: false, message: "", type: "info", position: isRtl ? "left" : "right" });
  const contentRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlertState({ open: true, type, message, position: isRtl ? "left" : "right" });
  }, [isRtl]);

  const categoryOptions = useMemo(
    () => normalizeAssetCardConfig(assetConfig).categories.map((category) => ({ value: category.value, label: category.label })),
    [assetConfig]
  );
  const assetTypeOptions = useMemo(
    () => getAssetTypesForCategory(assetConfig, formData.category_subcategory),
    [assetConfig, formData.category_subcategory]
  );
  const operationFields = getOperationalFieldsForAsset(formData.category_subcategory, formData.type);

  useEffect(() => {
    if (!contentRef.current) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      contentRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [currentStep]);

  useEffect(() => {
    if (!canSubmit || !org?.id) {
      setLoading(false);
      return;
    }

    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [devicesRes, configRes] = await Promise.all([
          axios.get(apiUrl(`device/filtered/${org.id}`)),
          axios.get(apiUrl("configuration/public")),
        ]);
        const deviceRows = Array.isArray(devicesRes.data?.data) ? devicesRes.data.data : [];
        setDevices(deviceRows);
        setAssetConfig(normalizeAssetCardConfig(configRes.data?.data?.assetCard));

        if (mode === "edit" && assetId) {
          const assetRes = await axios.get(apiUrl(`asset-card/${assetId}`), { params: { organization_id: org.id } });
          const assetData = assetRes.data?.data;
          if (!assetData) throw new Error(`Asset with ID ${assetId} was not found.`);
          const nextFormData = createInitialFormData();
          Object.assign(nextFormData, {
            device_id: assetData.device_id || null,
            asset_name: assetData.asset_name || "",
            asset_code: assetData.asset_code || "",
            category_subcategory: assetData.category_subcategory || "",
            brand: assetData.brand || "",
            model_number: assetData.model_number || "",
            serial_number: assetData.serial_number || "",
            type: assetData.type || "",
            manufacturer: assetData.manufacturer || "",
            manufacture_date: formatDate(assetData.manufacture_date),
            location: assetData.location || "",
            ac_capacity: formatValue(assetData.ac_capacity),
          });
          nextFormData.operational = { ...nextFormData.operational, ...(assetData.OperationalData || {}) };
          nextFormData.maintenance = {
            ...nextFormData.maintenance,
            ...(assetData.MaintenanceLifecycle || {}),
            installation_date: formatDate(assetData.MaintenanceLifecycle?.installation_date),
            warranty_expiry_date: formatDate(assetData.MaintenanceLifecycle?.warranty_expiry_date),
            last_maintenance_date: formatDate(assetData.MaintenanceLifecycle?.last_maintenance_date),
            next_maintenance_due: formatDate(assetData.MaintenanceLifecycle?.next_maintenance_due),
          };
          nextFormData.financial = { ...nextFormData.financial, ...(assetData.FinancialOwnership || {}) };
          nextFormData.iot = { ...nextFormData.iot, ...(assetData.IotMonitoring || {}) };
          setFormData(nextFormData);
          setSelectedDevice(assetData.Device || null);
        }
      } catch (err) {
        console.error(err);
        showAlert("error", getErrorMessage(err));
        if (mode === "edit") setState(false);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [assetId, canSubmit, mode, org?.id, setState, showAlert]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setSectionField = (section, name, value) => {
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section], [name]: value } }));
  };

  const handleNumberChange = (section, name, value) => {
    setSectionField(section, name, parseOptionalNumber(value));
  };

  const handleCategorySelect = (option) => {
    setFormData((prev) => ({
      ...prev,
      category_subcategory: option?.value || "",
      type: "",
      operational: { ...baseOperational },
    }));
  };

  const handleOperationalChange = (field, value) => {
    const nextValue = field.integer ? parseOptionalInteger(value) : field.type === "number" ? parseOptionalNumber(value) : value;
    setFormData((prev) => {
      const nextOperational = { ...prev.operational, [field.name]: nextValue };
      if (field.group === "compressors") {
        nextOperational.compressors = syncRowsToCount(prev.operational.compressors, nextValue, COMPRESSOR_FIELDS);
      }
      if (field.group === "indoor_fans") {
        nextOperational.indoor_fans = syncRowsToCount(prev.operational.indoor_fans, nextValue, FAN_FIELDS);
      }
      if (field.group === "outdoor_fans") {
        nextOperational.outdoor_fans = syncRowsToCount(prev.operational.outdoor_fans, nextValue, FAN_FIELDS);
      }
      return { ...prev, operational: nextOperational };
    });
  };

  const updateGroupRow = (groupName, index, field, value) => {
    setFormData((prev) => {
      const rows = [...(prev.operational[groupName] || [])];
      rows[index] = { ...(rows[index] || {}), [field.name]: field.type === "number" ? parseOptionalNumber(value) : value };
      return { ...prev, operational: { ...prev.operational, [groupName]: rows } };
    });
  };

  const validateStep = () => {
    if (currentStep !== 1) return true;
    if (!String(formData.asset_name || "").trim()) {
      showAlert("error", addT.validation?.assetNameRequired || "Asset name is required.");
      return false;
    }
    if (!formData.category_subcategory) {
      showAlert("error", "Category is required.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (submitting || !validateStep()) return;
    const payload = {
      organization_id: org.id,
      device_id: formData.device_id || null,
      asset_name: formData.asset_name,
      asset_code: formData.asset_code || null,
      category_subcategory: formData.category_subcategory,
      brand: formData.brand || null,
      model_number: formData.model_number || null,
      serial_number: formData.serial_number || null,
      type: formData.type || null,
      manufacturer: formData.manufacturer || null,
      manufacture_date: formData.manufacture_date || null,
      location: formData.location || null,
      ac_capacity: parseOptionalNumber(formData.ac_capacity),
      operational: sanitizeOperational(formData.operational),
      maintenance: sanitizeObject(formData.maintenance),
      financial: sanitizeObject(formData.financial),
      iot: sanitizeObject(formData.iot),
    };

    setSubmitting(true);
    try {
      const res = mode === "edit"
        ? await axios.put(apiUrl(`asset-card/${assetId}`), payload, { headers: { "Content-Type": "application/json" } })
        : await axios.post(apiUrl("asset-card"), payload, { headers: { "Content-Type": "application/json" } });
      const successMessage = res.data?.message || (mode === "edit" ? t.updateSuccess : t.createSuccess);
      setState(false);
      await refresh();
      if (onAlert) onAlert("success", successMessage);
      else showAlert("success", successMessage);
    } catch (err) {
      const errors = err.response?.data?.errors;
      showAlert("error", Array.isArray(errors) ? errors.map((item) => item.msg).join(" ") : getErrorMessage(err));
      setSubmitting(false);
    }
  };

  const renderInput = (field, value, onChange) => (
    <input
      type={field.type === "number" ? "number" : "text"}
      step={field.integer ? "1" : field.type === "number" ? "0.01" : undefined}
      min={field.type === "number" ? "0" : undefined}
      inputMode={field.integer ? "numeric" : field.type === "number" ? "decimal" : undefined}
      pattern={field.integer ? "[0-9]*" : undefined}
      className="add-column"
      name={field.name}
      value={formatValue(value)}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  const renderGroupRows = (groupName, rows, fields) => (
    <div className="asset-dynamic-group">
      {(rows || []).map((row, index) => (
        <div className="asset-dynamic-card" key={`${groupName}-${index}`}>
          <h4>{groupName === "compressors" ? `Compressor ${index + 1}` : `${groupName === "indoor_fans" ? "Indoor Fan" : "Outdoor Fan"} ${index + 1}`}</h4>
          {fields.map((field) => (
            <div className="form-item" key={field.name}>
              <label className="form-lbl">{field.label}</label>
              {renderInput(field, row[field.name], (value) => updateGroupRow(groupName, index, field, value))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Fragment>
            <p>{addT.step1.description}</p>
            <div className="form-item">
              <label className="form-lbl">{addT.step1.labelDevice}</label>
              <Select
                options={devices.map((device) => buildDeviceDropdownOption(device))}
                value={selectedDevice ? buildDeviceDropdownOption(selectedDevice) : null}
                onChange={(option) => {
                  setSelectedDevice(option);
                  setField("device_id", option?.value || null);
                }}
                placeholder={addT.step1.placeholderSelectDevice}
                styles={selectStyles}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                isClearable
              />
            </div>
            {[
              ["asset_name", addT.step1.labelAssetName, addT.step1.placeholderAssetName],
              ["asset_code", addT.step1.labelAssetCode, addT.step1.placeholderAssetCode],
            ].map(([name, label, placeholder]) => (
              <div className="form-item" key={name}>
                <label className="form-lbl">{label}</label>
                <input className="add-column" value={formData[name]} placeholder={placeholder} onChange={(event) => setField(name, event.target.value)} />
              </div>
            ))}
            <div className="form-item">
              <label className="form-lbl">Category</label>
              <Select
                options={categoryOptions}
                value={categoryOptions.find((option) => option.value === formData.category_subcategory) || null}
                onChange={handleCategorySelect}
                styles={selectStyles}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                isClearable
              />
            </div>
            <div className="form-item">
              <label className="form-lbl">Asset Type</label>
              <Select
                options={assetTypeOptions}
                value={assetTypeOptions.find((option) => option.value === formData.type) || null}
                onChange={(option) => {
                  setFormData((prev) => ({
                    ...prev,
                    type: option?.value || "",
                    operational: { ...baseOperational },
                  }));
                }}
                placeholder={formData.category_subcategory ? "Select asset type" : "Select category first"}
                styles={selectStyles}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                isDisabled={!formData.category_subcategory || assetTypeOptions.length === 0}
                isClearable
              />
            </div>
            {[
              ["brand", addT.step1.labelBrand, addT.step1.placeholderBrand],
              ["model_number", addT.step1.labelModelNumber, addT.step1.placeholderModelNumber],
              ["serial_number", addT.step1.labelSerialNumber, addT.step1.placeholderSerialNumber],
              ["manufacturer", addT.step1.labelManufacturer, addT.step1.placeholderManufacturer],
              ["location", addT.step1.labelLocation, addT.step1.placeholderLocation],
            ].map(([name, label, placeholder]) => (
              <div className="form-item" key={name}>
                <label className="form-lbl">{label}</label>
                <input className="add-column" value={formData[name]} placeholder={placeholder} onChange={(event) => setField(name, event.target.value)} />
              </div>
            ))}
            <div className="form-item">
              <label className="form-lbl">{addT.step1.labelManufactureDate}</label>
              <input type="date" className="add-column" value={formData.manufacture_date || ""} onChange={(event) => setField("manufacture_date", event.target.value)} />
            </div>
            <div className="form-item">
              <label className="form-lbl">AC Capacity</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="add-column"
                value={formatValue(formData.ac_capacity)}
                onChange={(event) => setField("ac_capacity", parseOptionalNumber(event.target.value))}
              />
            </div>
          </Fragment>
        );
      case 2:
        return (
          <Fragment>
            <p>{formData.category_subcategory ? addT.step2.description : "Select a category to show operational fields."}</p>
            {operationFields.map((field) => (
              <div className="form-item" key={field.name}>
                <label className="form-lbl">{field.label}</label>
                {renderInput(field, formData.operational[field.name], (value) => handleOperationalChange(field, value))}
              </div>
            ))}
            {renderGroupRows("compressors", formData.operational.compressors, COMPRESSOR_FIELDS)}
            {renderGroupRows("indoor_fans", formData.operational.indoor_fans, FAN_FIELDS)}
            {renderGroupRows("outdoor_fans", formData.operational.outdoor_fans, FAN_FIELDS)}
          </Fragment>
        );
      case 3:
        return (
          <Fragment>
            <p>{addT.step3.description}</p>
            {[
              ["installation_date", addT.step3.labelInstallationDate, "date"],
              ["warranty_expiry_date", addT.step3.labelWarrantyExpiryDate, "date"],
              ["condition_status", addT.step3.labelConditionStatus, "text"],
              ["technical_evaluation", addT.step3.labelTechnicalEvaluation, "textarea"],
              ["criticality_level", addT.step3.labelCriticalityLevel, "text"],
              ["estimated_life_cycle_years", addT.step3.labelEstimatedLifeCycleYears, "number"],
              ["last_maintenance_date", addT.step3.labelLastMaintenanceDate, "date"],
              ["next_maintenance_due", addT.step3.labelNextMaintenanceDue, "date"],
              ["o_and_m_vendor", addT.step3.labelOAndMVendor, "text"],
              ["depreciation", t.fieldDepreciation || "Depreciation", "number"],
            ].map(([name, label, type]) => (
              <div className="form-item" key={name}>
                <label className="form-lbl">{label}</label>
                {type === "textarea" ? (
                  <textarea className="add-column" rows="3" value={formData.maintenance[name] || ""} onChange={(event) => setSectionField("maintenance", name, event.target.value)} />
                ) : (
                  <input type={type} step={type === "number" ? "0.01" : undefined} className="add-column" value={formData.maintenance[name] || ""} onChange={(event) => type === "number" ? handleNumberChange("maintenance", name, event.target.value) : setSectionField("maintenance", name, event.target.value)} />
                )}
              </div>
            ))}
          </Fragment>
        );
      case 4:
        return (
          <Fragment>
            <p>{addT.step4.description}</p>
            {[
              ["supplier_vendor", addT.step4.labelSupplierVendor, "text"],
              ["department_owner", addT.step4.labelDepartmentOwner, "text"],
              ["purchase_cost", addT.step4.labelPurchaseCost, "number"],
              ["annual_om_cost", addT.step4.labelAnnualOmCost, "number"],
            ].map(([name, label, type]) => (
              <div className="form-item" key={name}>
                <label className="form-lbl">{label}</label>
                <input type={type} step={type === "number" ? "0.01" : undefined} className="add-column" value={formData.financial[name] || ""} onChange={(event) => type === "number" ? handleNumberChange("financial", name, event.target.value) : setSectionField("financial", name, event.target.value)} />
              </div>
            ))}
          </Fragment>
        );
      case 5:
        return (
          <Fragment>
            <p>{addT.step5.description}</p>
            {[
              ["monitored_parameters", addT.step5.labelMonitoredParameters, "textarea"],
              ["alert_thresholds", addT.step5.labelAlertThresholds, "textarea"],
              ["integration_protocol", addT.step5.labelIntegrationProtocol, "text"],
            ].map(([name, label, type]) => (
              <div className="form-item" key={name}>
                <label className="form-lbl">{label}</label>
                {type === "textarea" ? (
                  <textarea className="add-column" rows="3" value={formData.iot[name] || ""} onChange={(event) => setSectionField("iot", name, event.target.value)} />
                ) : (
                  <input className="add-column" value={formData.iot[name] || ""} onChange={(event) => setSectionField("iot", name, event.target.value)} />
                )}
              </div>
            ))}
          </Fragment>
        );
      default:
        return null;
    }
  };

  if (!canSubmit) {
    return (
      <div id="modal" style={{ direction: isRtl ? "rtl" : "ltr" }}>
        <div id="popup" className="dashboard-modal asset-modal">
          <div className="dashboard-header">
            <div className="dashboard-header-icon"><AddNewIcon /></div>
            <div className="dashboard-header-text">
              <h2>{t.permissionDenied?.title || "Permission Denied"}</h2>
              <p>{t.permissionDenied?.message || "You do not have permission."}</p>
            </div>
          </div>
          <div className="modal-footer dashboard-footer">
            <button className="btn btn-cancel" onClick={() => setState(false)}>{t.buttonCancel || "Close"}</button>
          </div>
        </div>
      </div>
    );
  }

  const stepsMeta = [1, 2, 3, 4, 5].map((id) => ({
    id,
    label: addT[`step${id}`]?.shortLabel || `Step ${id}`,
  }));
  const currentStepConfig = addT[`step${currentStep}`] || {};

  return (
    <div id="modal" style={{ direction: isRtl ? "rtl" : "ltr" }}>
      <Alert open={alertState.open} close={() => setAlertState({ ...alertState, open: false })} message={alertState.message} type={alertState.type} position={alertState.position} />
      <div id="popup" className="dashboard-modal asset-modal tall">
        <div className="dashboard-header">
          <div className="dashboard-header-icon"><AddNewIcon /></div>
          <div className="dashboard-header-text">
            <h2>{t.title}</h2>
            <p>{currentStepConfig.header || currentStepConfig.description || ""}</p>
          </div>
        </div>
        <div className="modal-divider" />
        {loading ? (
          <div className="modal-content dashboard-content"><Spinner /></div>
        ) : (
          <Fragment>
            <div className="asset-stepper">
              <div className="asset-stepper-track">
                {stepsMeta.map((step) => (
                  <div key={step.id} className="asset-step">
                    <StepCircle status={currentStep > step.id ? "done" : currentStep === step.id ? "current" : "upcoming"} />
                  </div>
                ))}
              </div>
              <div className="asset-stepper-labels">
                {stepsMeta.map((step) => <div key={step.id} className="asset-step-label">{step.label}</div>)}
              </div>
            </div>
            <div className="modal-content dashboard-content" ref={contentRef}>{renderStep()}</div>
            <div className="modal-footer dashboard-footer">
              <button className="btn btn-cancel" onClick={() => setState(false)} disabled={submitting}>{t.buttonCancel}</button>
              <div className="modal-footer-right-buttons">
                {currentStep > 1 && <button className="btn btn-cancel btn-back" onClick={() => setCurrentStep((prev) => prev - 1)} disabled={submitting}>{t.buttonBack}</button>}
                {currentStep < 5 && <button className="btn btn-apply dashboard-primary-btn" onClick={() => validateStep() && setCurrentStep((prev) => prev + 1)}>{t.buttonNext}</button>}
                {currentStep === 5 && <button className="btn btn-apply dashboard-primary-btn" onClick={submit} disabled={submitting}>{mode === "edit" ? t.buttonUpdate : t.buttonCreate}</button>}
              </div>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default AssetCardFormModal;
